/*
 * The caps the tray refuses against, and the pre-flight that applies them.
 *
 * The numbers are the server's own: they arrive in `rt.hello` and live in the model (protocol.md
 * §2.5.1), so a cap that is raised on the server is raised in the browser on the next connection
 * rather than on the next release. The fallbacks below are only for the moment before the socket
 * has answered — a person who drops a file into a cold shell still gets a sensible refusal instead
 * of an upload the server will throw away.
 *
 * Refusing here is not a substitute for the server's refusal, it is a kindness: the server's answer
 * to an over-sized body is written while the browser is still uploading, and the connection reset
 * that follows discards it (spec 07 §3.1).
 */

import { formatBytes } from "../../i18n/format.ts"
import { client } from "../../store/store.ts"

export interface AttachmentLimits {
	maxFileBytes: number
	maxFilesPerMessage: number
	maxMessageBytes: number
}

/** The defaults of `[files]` (spec 07 §4 "Limits"), used only until the socket has said otherwise. */
export const FALLBACK_LIMITS: AttachmentLimits = {
	maxFileBytes: 100_000_000,
	maxFilesPerMessage: 10,
	maxMessageBytes: 250_000_000,
}

/** The caps this instance serves, or the fallbacks while the model has none. */
export function attachmentLimits(): AttachmentLimits {
	const limits = client.api.getState().limits
	if (limits === null) return FALLBACK_LIMITS
	// A zero is a limit the server did not send, not a limit of zero.
	return {
		maxFileBytes: limits.maxFileBytes || FALLBACK_LIMITS.maxFileBytes,
		maxFilesPerMessage: limits.maxAttachmentsPerMessage || FALLBACK_LIMITS.maxFilesPerMessage,
		maxMessageBytes: limits.maxMessageBytes || FALLBACK_LIMITS.maxMessageBytes,
	}
}

/** Why a file was not staged; every reason names the number it broke. */
export interface Refusal {
	name: string
	key: string
	args: Record<string, string | number>
}

export interface Admission {
	accepted: File[]
	refused: Refusal[]
}

/**
 * Which of the dropped files the tray takes. The three caps are checked in the order a person meets
 * them — this file is too big, this message would carry too many files, these files are too much
 * together — and each refusal names the file it is about, because a batch of ten that fails as one
 * sentence tells nobody which one to remove.
 *
 * A directory dropped onto the page arrives as a zero-byte entry with no type; it is refused as
 * unreadable rather than uploaded as an empty file.
 */
export function admit(
	files: readonly File[],
	staged: { count: number; bytes: number },
	limits: AttachmentLimits = attachmentLimits(),
): Admission {
	const accepted: File[] = []
	const refused: Refusal[] = []
	let count = staged.count
	let bytes = staged.bytes
	for (const file of files) {
		if (file.size > limits.maxFileBytes) {
			refused.push({
				name: file.name,
				key: "composer.attach.error.tooLarge",
				args: { name: file.name, limit: formatBytes(limits.maxFileBytes) },
			})
			continue
		}
		if (count + 1 > limits.maxFilesPerMessage) {
			refused.push({
				name: file.name,
				key: "composer.attach.error.tooMany",
				args: { name: file.name, limit: limits.maxFilesPerMessage },
			})
			continue
		}
		if (bytes + file.size > limits.maxMessageBytes) {
			refused.push({
				name: file.name,
				key: "composer.attach.error.messageTooLarge",
				args: { name: file.name, limit: formatBytes(limits.maxMessageBytes) },
			})
			continue
		}
		accepted.push(file)
		count += 1
		bytes += file.size
	}
	return { accepted, refused }
}
